import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import gsap from 'gsap';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #07071A;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
`;

const Ring = styled.svg`
  width: 72px;
  height: 72px;
  transform: rotate(-90deg);

  circle.track {
    fill: none;
    stroke: rgba(34, 211, 238, 0.1);
    stroke-width: 2;
  }

  circle.fill {
    fill: none;
    stroke: #22D3EE;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-dasharray: ${Math.PI * 2 * 33};
    stroke-dashoffset: ${({ $progress }) =>
      Math.PI * 2 * 33 * (1 - $progress / 100)};
    transition: stroke-dashoffset 0.05s linear;
    filter: drop-shadow(0 0 6px #22D3EE);
  }
`;

const Counter = styled.div`
  font-family: 'Space Grotesk', monospace;
  font-size: clamp(72px, 14vw, 120px);
  font-weight: 700;
  letter-spacing: -0.04em;
  color: #E2E8F0;
  line-height: 1;
  position: relative;

  span.accent {
    color: #22D3EE;
  }
`;

const SubText = styled.p`
  font-family: 'Space Grotesk', monospace;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(148, 163, 184, 0.55);
`;

const ProgressBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  width: ${({ $progress }) => $progress}%;
  background: linear-gradient(to right, transparent, #22D3EE);
  box-shadow: 0 0 14px #22D3EE;
  transition: width 0.06s linear;
`;

const MESSAGES = [
  '// brewing magic...',
  '// loading portfolio...',
  '// almost there...',
];

const Loader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);
  const overlayRef = useRef(null);

  useEffect(() => {
    const obj = { val: 0 };

    const msgInterval = setInterval(() => {
      setMsgIdx(i => (i + 1) % MESSAGES.length);
    }, 900);

    const tween = gsap.to(obj, {
      val: 100,
      duration: 2.4,
      ease: 'power2.inOut',
      onUpdate() {
        setProgress(Math.round(obj.val));
      },
      onComplete() {
        clearInterval(msgInterval);
        gsap.to(overlayRef.current, {
          yPercent: -102,
          duration: 0.75,
          ease: 'power4.inOut',
          delay: 0.25,
          onComplete,
        });
      },
    });

    return () => {
      tween.kill();
      clearInterval(msgInterval);
    };
  }, [onComplete]);

  return (
    <Overlay ref={overlayRef}>
      <Ring viewBox="0 0 72 72" $progress={progress}>
        <circle className="track" cx="36" cy="36" r="33" />
        <circle className="fill" cx="36" cy="36" r="33" />
      </Ring>

      <Counter>
        {String(progress).padStart(2, '0')}
        <span className="accent">%</span>
      </Counter>

      <SubText>{MESSAGES[msgIdx]}</SubText>

      <ProgressBar $progress={progress} />
    </Overlay>
  );
};

export default Loader;
